/**
 * Componente seletor de profissionais com cards visuais.
 */

import type { PublicProvider } from "../types";
import { FiCheck, FiStar, FiUser, FiUsers } from "react-icons/fi";
import { resolveImageUrl, todayYMD } from "../utils";
import { getProviderInitials } from "../utils/publicPeople";

interface ProviderSelectorProps {
  providers: PublicProvider[];
  selectedProviderId: string;
  onProviderChange: (providerId: string) => void;
  date: string;
  slots?: string[];
  providerSlots?: Record<string, string[]>;
  loadingSlots?: boolean;
  loadingProviderSlots?: boolean;
  embedded?: boolean;
  allowAnyProvider?: boolean;
}

export function ProviderSelector({
  providers,
  selectedProviderId,
  onProviderChange,
  date,
  slots = [],
  providerSlots = {},
  loadingSlots = false,
  loadingProviderSlots = false,
  embedded = false,
  allowAnyProvider = true,
}: ProviderSelectorProps) {
  if (providers.length === 0) return null;

  const selectedAnyProvider = allowAnyProvider && selectedProviderId === "";
  const isToday = date === todayYMD();
  const formattedDate = new Intl.DateTimeFormat("pt-BR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  }).format(new Date(`${date}T12:00:00`));

  const getSlotHint = (availableSlots: string[], loading: boolean) => {
    const nextSlot = availableSlots[0];

    if (loading) {
      return {
        text: "Buscando horários...",
        available: false,
      };
    }

    if (nextSlot) {
      return {
        text: isToday ? `Hoje às ${nextSlot}` : `${formattedDate} às ${nextSlot}`,
        available: true,
      };
    }

    return {
      text: isToday ? "Sem horários hoje" : `Sem horários em ${formattedDate}`,
      available: false,
    };
  };

  const anyProviderHint = getSlotHint(slots, loadingSlots);
  const slotHintClassName = (available: boolean) =>
    `mt-2 text-sm font-semibold ${available ? "text-emerald-700" : "text-slate-500"}`;

  return (
    <section
      className={
        embedded
          ? "animate-fade-in overflow-hidden"
          : "animate-fade-in overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm shadow-slate-200/70 sm:p-6"
      }
    >
      {!embedded && (
        <div className="mb-4 flex items-center gap-2">
          <FiUsers className="h-5 w-5 text-slate-600" aria-hidden="true" />
          <h2 className="text-3xl font-bold leading-tight text-[#233055]">
            Escolha o profissional
          </h2>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {allowAnyProvider ? (
          <button
            type="button"
            onClick={() => onProviderChange("")}
            aria-pressed={selectedAnyProvider}
            className={`group relative rounded-2xl border bg-white p-4 text-left transition-all duration-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 ${
              selectedAnyProvider
                ? "border-primary-500 shadow-[0_0_0_1px_rgba(124,58,237,0.35)]"
                : "border-slate-200 hover:border-primary-300"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full text-lg ${
                  selectedAnyProvider
                    ? "bg-primary-100 text-primary-700"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                <FiUsers aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="line-clamp-2 text-xl font-semibold leading-tight text-[#233055]">
                  Qualquer profissional
                </h3>
                <p className="mt-1 text-base text-slate-600">Equipe especializada</p>
              </div>
              {selectedAnyProvider && (
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary-600 text-white shadow-sm">
                  <FiCheck aria-hidden="true" />
                </span>
              )}
            </div>
            <p className={slotHintClassName(anyProviderHint.available)}>
              {anyProviderHint.text}
            </p>
          </button>
        ) : null}

        {providers.map((provider) => {
          const selected = selectedProviderId === provider.id;
          const specialization =
            String(provider.description || "").trim() || "Profissional especialista";
          const reviewsCount = Number(provider.reviewsCount || 0);
          const averageRating =
            typeof provider.averageRating === "number" && reviewsCount > 0
              ? Math.min(5, Math.max(1, provider.averageRating))
              : null;
          const ratingLabel =
            averageRating !== null
              ? `${averageRating.toLocaleString("pt-BR", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })} (${reviewsCount})`
              : "Ainda sem avaliações";
          const providerHint = getSlotHint(
            providerSlots[provider.id] || [],
            loadingProviderSlots
          );

          return (
            <button
              key={provider.id}
              type="button"
              onClick={() => onProviderChange(provider.id)}
              aria-pressed={selected}
              className={`group relative rounded-2xl border bg-white p-4 text-left transition-all duration-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 ${
                selected
                  ? "border-primary-500 shadow-[0_0_0_1px_rgba(124,58,237,0.35)]"
                  : "border-slate-200 hover:border-primary-300"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-sm font-bold text-slate-700">
                  {provider.imageUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={resolveImageUrl(provider.imageUrl)}
                      alt={`Foto de ${provider.name}`}
                      className="h-full w-full object-cover"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    getProviderInitials(provider.name)
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <h3
                    className="line-clamp-2 text-xl font-semibold leading-tight text-[#233055]"
                    title={provider.name}
                  >
                    {provider.name}
                  </h3>
                  <p
                    className="mt-1 line-clamp-1 text-base text-slate-600"
                    title={specialization}
                  >
                    {specialization}
                  </p>
                  <p className="mt-1 inline-flex items-center gap-1 text-sm text-slate-500">
                    <FiStar className="text-amber-500" aria-hidden="true" />
                    {ratingLabel}
                  </p>
                </div>

                {selected && (
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary-600 text-white shadow-sm">
                    <FiCheck aria-hidden="true" />
                  </span>
                )}
              </div>

              <p className={slotHintClassName(providerHint.available)}>
                {providerHint.text}
              </p>
            </button>
          );
        })}
      </div>

      <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
        <p className="flex items-center gap-2">
          <FiUser className="text-primary-600" aria-hidden="true" />
          Ao escolher um profissional, mostramos apenas os serviços atendidos por ele.
        </p>
      </div>
    </section>
  );
}
